import {
  ContainerEntity,
  containerStatusSelector,
  useGetContainerStatusQuery,
  useGetWellsByContainerQuery,
  wellsSelector
} from "@ots-frontend/ots-data";
import {forwardRef, useMemo} from "react";
import {
  ActionIcon,
  Anchor,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Modal,
  Skeleton,
  Text,
  Title,
  Tooltip
} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {Empty} from "../../empty/Empty/Empty";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  IconHierarchy,
  IconInfoCircle,
  IconSearch,
  IconTruck
} from "@tabler/icons-react";
import {ContainerStatusChart} from "../../chart/ContainerStatusChart/ContainerStatusChart";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {ObjectMappingDataTableCard} from "../../card/ObjectMappingDataTableCard/ObjectMappingDataTableCard"
import {ObjectMeterDataTableCard} from "../../card/ObjectMeterDataTableCard/ObjectMeterDataTableCard"
import {Link, useNavigate} from "react-router-dom";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {RestrictionDataTableCard} from "../../card/RestrictionDataTableCard/RestrictionDataTableCard";
import {WellDataTable} from "../../data-table/WellDataTable/WellDataTable";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {useDisclosure} from "@mantine/hooks";
import {EntityGraphView} from "../../graph/EntityGraphView/EntityGraphView";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {ContainerStatusIcon} from "../../icon/ContainerStatusIcon/ContainerStatusIcon";

export type ContainerDetailsProps = {
  container: ContainerEntity
}

export const ContainerDetails = (props: ContainerDetailsProps) => {
  const {container} = props;

  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp()
  const navigate = useNavigate()

  const [opened, {close, open}] = useDisclosure(false);

  const selector = useMemo(() => {
    return containerStatusSelector()
  }, []);

  const {status, isFetchingStatus} = useGetContainerStatusQuery(regionId ? {
    containerId: container.id,
    regionId: regionId,
  } : skipToken, {
    selectFromResult: (result) => ({
      status: selector(result),
      isFetchingStatus: result.isFetching
    })
  });

  const selectWells = useMemo(() => {
    return wellsSelector();
  }, []);

  const {wells, isWellsLoading} = useGetWellsByContainerQuery(regionId ? {
    regionId: regionId,
    containerId: container.id,
  } : skipToken, {
    selectFromResult: (result) => ({
      wells: selectWells(result, {showDoNotUse: true}),
      isWellsLoading: result.isLoading
    })
  });

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.container.info.section.details.label", "Details")}</Title>
            <ActionIcon onClick={() => open()}>
              <IconHierarchy size="1.125rem"/>
            </ActionIcon>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.abbr.label", "Abbr.")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.abbr}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.qrcode.label", "QR Code")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.barcode}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.leaseid.label", "Lease ID")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.leaseId}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.leasename.label", "Lease Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.leaseName}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.afe.label", "AFE")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.afe &&
                  <>
                    {container.afePrefix && container.afePrefix}
                    <Text fw={700} inherit={true} span={true}>
                      {` ${container.afe} `}
                    </Text>
                    {container.afeSuffix && container.afeSuffix}
                  </>
                }
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.freighttype.label", "Freight Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.waterTypeId ?
                  <DefaultText defaultValue={container.waterTypeName}/> : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.type.label", "Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.type ? <FormattedDatabaseCode value={container.type}/> : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.scadamode.label", "Scada Mode")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.scadaModeReason ?
                  (
                    <Group spacing="xs">
                      <FormattedDatabaseCode value={container.scadaMode}/>
                      <Tooltip label={container.scadaModeReason} withinPortal={true}>
                        <IconInfoCircle size="14"/>
                      </Tooltip>
                    </Group>
                  )
                  : (
                    <FormattedDatabaseCode value={container.scadaMode}/>
                  )
                }
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.dispatchable.label", "Dispatchable")}
              </Text>
              <Text ta="left" c={container.doNotDispatch ? "service-work" : "dimmed"} fz="xs">
                <InnerDnd container={container}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.usable.label", "Usable")}
              </Text>
              <Text ta="left" c={container.doNotUse ? "service-work" : "dimmed"} fz="xs">
                <InnerDnu container={container}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.dispatchignoremaxnonprimaryvolume.label", "Dispatch - Ignore Max Primary Volume")}
              </Text>
              <Text ta="left" c={container.dispatchIgnoreMaxNonPrimaryVolume ? "service-work" : "dimmed"}
                    fz="xs">
                <Group spacing="xs">
                  {container.dispatchIgnoreMaxNonPrimaryVolume &&
                    <ContainerStatusIcon name="ignore-max-non-primary-volume" width={14} height={14}/>}
                  {container.dispatchIgnoreMaxNonPrimaryVolume ? "Yes" : "No"}
                </Group>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant="dashed"/>
            </Grid.Col>
            {container.containerClusterId &&
              <>
                <Grid.Col span={12}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.container.info.field.containercluster.label", "Cluster")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: container.containerClusterId,
                        objectName: container.containerClusterName,
                        objectType: "CONCOMMIGLED",
                      }}/>
                  </Text>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Divider variant="dashed"/>
                </Grid.Col>
              </>
            }
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.site.label", "Site")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {container.siteId ? (
                  <Anchor component={Link} to={`/sites/profile?id=${container.siteId}`}>
                    <DefaultText value={container.siteName}/>
                  </Anchor>
                ) : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.sitetransportviapipeline.label", "Site Transport Water Via")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <Group spacing="xs">
                  {container.siteId ? (container.siteTransportViaPipeline ?
                    <ContainerStatusIcon name="water-pipeline" width={14} height={14}/> :
                    <IconTruck size="14"/>) : undefined}
                  {container.siteId ? (container.siteTransportViaPipeline ? "Pipeline" : "Truck") : undefined}
                </Group>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.container.info.field.sitetransportoilviapipeline.label", "Site Transport Oil Via")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <Group spacing="xs">
                  {container.siteId ? (container.siteTransportOilViaPipeline ?
                    <ContainerStatusIcon name="oil-pipeline" width={14} height={14}/> :
                    <IconTruck size="14"/>) : undefined}
                  {container.siteId ? (container.siteTransportOilViaPipeline ? "Pipeline" : "Truck") : undefined}
                </Group>
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.container.info.section.status.label", "Status")}</Title>
            <ActionIcon onClick={() => navigate(`/containers/scada?containerId=${container.id}`)}>
              <IconSearch size="1.125rem"/>
            </ActionIcon>
          </Group>

          <Divider/>

          <Grid>
            {isFetchingStatus ? (
              <Grid.Col span={12} m="sm">
                <Skeleton height={50} circle mb="xl"/>
                <Skeleton height={8} radius="xl"/>
                <Skeleton height={8} mt={6} radius="xl"/>
                <Skeleton height={8} mt={6} width="70%" radius="xl"/>
              </Grid.Col>
            ) : (
              status ? (
                  <Container w={"100%"} h={400} m="sm" fluid={true}>
                    <ContainerStatusChart id={'container_status_chart_' + container.id} status={status}/>
                  </Container>
                )
                : (
                  <Empty
                    maw={400}
                    h={100}
                    mx="auto"
                    m="sm"
                    message={"No Status"}/>
                )
            )
            }
          </Grid>
        </Card>
      </Grid.Col>

      <HasAccess permissions={["restriction_view"]}>
        <Grid.Col span={12}>
          <RestrictionDataTableCard entity={container}/>
        </Grid.Col>
      </HasAccess>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>{t("common.entity.well.plural", "Wells")}</Title>
          </Group>
          <Divider pb="sm"/>
          <WellDataTable
            withBorder
            records={wells}
            fetching={isWellsLoading}
          />
        </Card>
      </Grid.Col>

      <HasAccess permissions={["meter_view"]}>
        <Grid.Col span={12}>
          <ObjectMeterDataTableCard entity={container}/>
        </Grid.Col>
      </HasAccess>

      <HasAccess permissions={["object_mapping_view"]}>
        <Grid.Col span={12}>
          <ObjectMappingDataTableCard entity={container}/>
        </Grid.Col>
      </HasAccess>

      {opened &&
        <Modal opened={opened} onClose={close} size="auto" title="Entity Graph">
          <Container h={700} w={800}>
            <EntityGraphView entity={container}/>
          </Container>
        </Modal>
      }
    </Grid>
  );
};

type InnerDnuProps = {
  container: ContainerEntity,
}

export const InnerDnu = forwardRef<HTMLDivElement, InnerDnuProps>((props, ref) => {
  const {container} = props;

  return (
    <Group spacing="xs" ref={ref}>
      {container.doNotUse &&
        <ContainerStatusIcon
          name={container.clearDoNotUseScheduledDttm ? "do-not-use-scheduled" : "do-not-use"}
          width={14}
          height={14}
        />}
      {container.doNotUse ? "No" : "Yes"}
      {(container.doNotUse && container.clearDoNotUseScheduledDttm) &&
        <span>-- Resets at <FormattedDateTime date={container.clearDoNotUseScheduledDttm}/></span>
      }
    </Group>
  )
})

type InnerDndProps = {
  container: ContainerEntity,
}

export const InnerDnd = forwardRef<HTMLDivElement, InnerDndProps>((props, ref) => {
  const {container} = props;

  return (
    <Group spacing="xs" ref={ref}>
      {container.doNotDispatch &&
        <ContainerStatusIcon
          name={container.clearDoNotDispatchScheduledDttm ? "do-not-dispatch-scheduled" : "do-not-dispatch"}
          width={14}
          height={14}
        />}
      {container.doNotDispatch ? "No" : "Yes"}
      {(container.doNotDispatch && container.clearDoNotDispatchScheduledDttm) &&
        <span>-- Resets at <FormattedDateTime date={container.clearDoNotDispatchScheduledDttm}/></span>
      }
    </Group>
  )
})
